import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Square, Clock, MapPin, User, CheckCircle2 } from "lucide-react";

const columns = [
  { key: "scheduled", label: "Scheduled", dot: "bg-accent" },
  { key: "in_progress", label: "In Progress", dot: "bg-[#E9C46A]" },
  { key: "completed", label: "Completed", dot: "bg-primary" },
];
const fmtMin = (m) => { const h = Math.floor(m / 60); const mm = Math.round(m % 60); return h > 0 ? `${h}h ${mm}m` : `${mm}m`; };

export default function Dispatch() {
  const [jobs, setJobs] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [now, setNow] = useState(Date.now());

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const dayJobs = jobs.filter((j) => j.scheduled_date === date);

  const memberMinG = (c) => (Number(c.minutes) || 0) + (c.clock_in_at ? Math.max(0, (now - Date.parse(c.clock_in_at)) / 60000) : 0);
  const planHours = (j) => {
    try {
      const [sh, sm] = j.start_time.split(":").map(Number);
      const [eh, em] = j.end_time.split(":").map(Number);
      const d = (eh * 60 + em) - (sh * 60 + sm);
      return d > 0 ? d / 60 : 1;
    } catch { return 1; }
  };
  const dayRevenue = dayJobs.reduce((a, j) => a + (j.price || 0), 0);
  const dayProjLabor = dayJobs.reduce((a, j) => a + planHours(j) * (j.crew || []).reduce((s, c) => s + (Number(c.hourly_rate) || 0), 0), 0);
  const dayLiveLabor = dayJobs.reduce((a, j) => a + (j.crew || []).reduce((s, c) => s + (memberMinG(c) / 60) * (Number(c.hourly_rate) || 0), 0), 0);
  const dayCrewOn = dayJobs.reduce((a, j) => a + (j.crew || []).filter((c) => c.clock_in_at).length, 0);
  const projProfit = dayRevenue - dayProjLabor;
  const liveProfit = dayRevenue - dayLiveLabor;

  const setStatus = async (j, status) => {
    try {
      await api.put(`/jobs/${j.id}/status`, { status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update job");
    }
  };
  const clockInMember = async (j, uid) => {
    try { await api.post(`/jobs/${j.id}/crew/${uid}/clock-in`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clock in"); }
  };
  const clockOutMember = async (j, uid) => {
    try { await api.post(`/jobs/${j.id}/crew/${uid}/clock-out`); toast.success("Time logged"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clock out"); }
  };

  return (
    <div className="space-y-6" data-testid="dispatch-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <p className="text-sm text-muted-foreground">Track real-time crew progress and log time in the field.</p>
        <div className="flex items-center gap-2">
          <Input type="date" data-testid="dispatch-date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" />
          <Button variant="ghost" size="sm" onClick={() => setDate(new Date().toISOString().slice(0, 10))}>Today</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="day-profit-summary">
        <Card className="p-4"><div className="overline text-muted-foreground">Day Revenue</div><div className="font-display text-2xl font-black mt-1 tabular-nums">{money(dayRevenue)}</div><div className="text-xs text-muted-foreground mt-0.5">{dayJobs.length} jobs</div></Card>
        <Card className="p-4"><div className="overline text-muted-foreground">Projected Profit</div><div className="font-display text-2xl font-black mt-1 tabular-nums">{money(projProfit)}</div><div className="text-xs text-muted-foreground mt-0.5">est. labor {money(dayProjLabor)}</div></Card>
        <Card className="p-4"><div className="overline text-muted-foreground flex items-center gap-1">Labor Burned {dayCrewOn > 0 && <span className="text-secondary">●</span>}</div><div className="font-display text-2xl font-black mt-1 tabular-nums" data-testid="day-live-labor">{money(dayLiveLabor)}</div><div className="text-xs text-muted-foreground mt-0.5">{dayCrewOn} clocked in now</div></Card>
        <Card className={`p-4 ${liveProfit >= 0 ? "bg-primary text-primary-foreground" : "bg-destructive text-white"}`}><div className="overline opacity-80 flex items-center gap-1">Live Profit {dayCrewOn > 0 && <span>●</span>}</div><div className="font-display text-2xl font-black mt-1 tabular-nums" data-testid="day-live-profit">{money(liveProfit)}</div><div className="text-xs opacity-80 mt-0.5">ticks down as crews work</div></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map((col) => {
          const list = dayJobs.filter((j) => j.status === col.key);
          return (
            <div key={col.key} data-testid={`dispatch-col-${col.key}`} className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <span className={`h-2.5 w-2.5 rounded-full ${col.dot}`} />
                <span className="font-display font-bold text-sm">{col.label}</span>
                <span className="text-xs text-muted-foreground ml-auto tabular-nums">{list.length}</span>
              </div>
              <div className="space-y-3 min-h-[6rem]">
                {list.map((j) => {
                  const done = (j.checklist || []).filter((c) => c.done).length;
                  const crew = j.crew || [];
                  const memberMin = (c) => (Number(c.minutes) || 0) + (c.clock_in_at ? Math.max(0, (now - Date.parse(c.clock_in_at)) / 60000) : 0);
                  const totalMin = crew.reduce((a, c) => a + memberMin(c), 0);
                  const laborCost = crew.reduce((a, c) => a + (memberMin(c) / 60) * (Number(c.hourly_rate) || 0), 0);
                  const anyRunning = crew.some((c) => c.clock_in_at);
                  const jobProfit = (j.price || 0) - laborCost;
                  return (
                    <Card key={j.id} data-testid="dispatch-job" className="p-4 border-border rounded-lg bg-card">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{j.title}</div>
                          <div className="text-xs text-muted-foreground">{j.start_time} · {j.service_type}</div>
                        </div>
                        <span className="text-sm font-semibold tabular-nums">{money(j.price)}</span>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                        {j.customer_name && <div className="flex items-center gap-1.5"><User className="h-3 w-3" /> {j.customer_name}</div>}
                        {j.address && <div className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3" /> {j.address}</div>}
                      </div>

                      {crew.length === 0 ? (
                        <div className="mt-3 text-xs text-muted-foreground rounded-md bg-muted/50 p-2.5">
                          No crew assigned — add crew in Schedule to track time & labor.
                        </div>
                      ) : (
                        <div className="mt-3 rounded-md bg-muted/50 p-2.5 space-y-2" data-testid="crew-time-panel">
                          {crew.map((c) => {
                            const running = !!c.clock_in_at;
                            return (
                              <div key={c.user_id} data-testid="crew-time-row" className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold truncate">{c.name}</div>
                                  <div className="text-[0.65rem] text-muted-foreground">
                                    ${c.hourly_rate}/hr · <span className={running ? "text-secondary font-semibold" : ""}>{fmtMin(memberMin(c))}{running ? " ●" : ""}</span>
                                  </div>
                                </div>
                                {running ? (
                                  <Button size="sm" variant="outline" data-testid="crew-clock-out-btn" onClick={() => clockOutMember(j, c.user_id)} className="h-6 px-2 text-[0.65rem]"><Square className="h-3 w-3 mr-1" /> Out</Button>
                                ) : (
                                  <Button size="sm" variant="outline" data-testid="crew-clock-in-btn" onClick={() => clockInMember(j, c.user_id)} className="h-6 px-2 text-[0.65rem]"><Play className="h-3 w-3 mr-1" /> In</Button>
                                )}
                              </div>
                            );
                          })}
                          <div className="pt-2 border-t border-border space-y-1">
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Total time</span><span className="tabular-nums font-semibold" data-testid="job-elapsed">{fmtMin(totalMin)}{anyRunning ? " ●" : ""}</span></div>
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground">Labor cost</span><span className="tabular-nums" data-testid="labor-cost">{money(laborCost)}</span></div>
                            <div className="flex justify-between text-xs font-semibold"><span>Job profit</span><span className={`tabular-nums ${jobProfit >= 0 ? "text-primary" : "text-destructive"}`} data-testid="job-profit">{money(jobProfit)}</span></div>
                          </div>
                        </div>
                      )}
                      {(j.checklist || []).length > 0 && <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3" /> {done}/{j.checklist.length} tasks</div>}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {col.key !== "completed" && (
                          <Button size="sm" data-testid="complete-job-btn" onClick={() => setStatus(j, "completed")} className="h-7 text-xs bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Complete</Button>
                        )}
                        {col.key === "completed" && (
                          <Button size="sm" variant="ghost" onClick={() => setStatus(j, "scheduled")} className="h-7 text-xs">Reopen</Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
                {list.length === 0 && <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">None</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
